import { faSortNumericUp, faTh } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Col, InfoList, Row } from '@orobix/orobix-fe-components'
import { FormikErrors } from 'formik'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { DatasetSelectField } from '../../components/DatasetSelect'
import ObservationsSelectField from '../../components/ObservationsSelectField/ObservationsSelectField'
import { USER_LEVEL } from '../../components/Settings/definitions'
import { SettingsContext } from '../../components/Settings/SettingsContext'
import { useStore } from '../../store/store'
import { createTraining, Metadata } from '../../store/storeActions'
import { BackboneSelectOption } from '../../types/backbone'
import {
  countEmptyAnnotationObservations,
  countNotAnnotatedObservations,
  countValidAnnotationObservations,
} from '../../utils/forms'
import BackboneSelect from './BackboneSelect/BackboneSelect'
import ClassesPriorityModal from './ClassesPriorityModal'
import { MAX_TRAINING_IMAGES } from './const'
import FP16Switch from './FP16Switch'
import PatchesConfigurationModal from './PatchesConfigurationModal/PatchesConfigurationModal'
import PatchStrategySelect from './PatchStrategySelect/PatchStrategySelect'
import TrainingNewForm, {
  SelectOption,
  TrainingNewFormValues,
} from './TrainingNewForm'
import { getEstimatedTrainingsDuration, ITrainingDuration } from './utils'

export type PatchFormValues = TrainingNewFormValues & {
  horizontalPatches?: number
  verticalPatches?: number
  patchesOverlap?: number
  patchesStrategy?: SelectOption
  patchesClassPriority?: string[]
}

interface RouteParams {
  projectId: string
  productId: string
}

const requiredImages = 5

const NewPatchTraining = () => {
  const { t } = useTranslation()
  const { product, productClasses } = useStore()
  const { settings } = useContext(SettingsContext)
  const history = useHistory()
  const { productId, projectId } = useParams<RouteParams>()
  const [classesPriorityModal, setClassesPriorityModal] = useState(false)
  const [patchesConfigurationModal, setPatchesConfigurationModal] = useState(
    false
  )
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([])
  const [estimatedTrainingsDuration, setEstimatedTrainingsDuration] = useState<
    ITrainingDuration[]
  >([])
  const [
    hasNonAnnotatedImagesInDataset,
    setHasNonAnnotatedImagesInDataset,
  ] = useState(false)

  return product && productClasses ? (
    <TrainingNewForm<PatchFormValues>
      productType={product.type}
      productId={productId}
      requiredImages={requiredImages}
      initialValues={{
        selectedObservations: [],
        horizontalPatches: 2,
        verticalPatches: 2,
        patchesOverlap: 0,
        patchesClassPriority: productClasses.results.map((i) => i.productClass),
        name: '',
        notes: '',
        startTest: false,
        fp16: true,
      }}
      renderRightColumn={({ values, setFieldValue, isSubmitting }) => (
        <>
          <InfoList
            list={[
              ...(countNotAnnotatedObservations(values.selectedObservations) > 0
                ? [
                    {
                      title: t('Not annotated'),
                      value: countNotAnnotatedObservations(
                        values.selectedObservations
                      ),
                    },
                  ]
                : []),
              ...(countEmptyAnnotationObservations(
                values.selectedObservations
              ) > 0
                ? [
                    {
                      title: t('Empty'),
                      value: countEmptyAnnotationObservations(
                        values.selectedObservations
                      ),
                    },
                  ]
                : []),
              ...(countValidAnnotationObservations(
                values.selectedObservations
              ) > 0
                ? [
                    {
                      title: t('Annotated'),
                      value: countValidAnnotationObservations(
                        values.selectedObservations
                      ),
                    },
                  ]
                : []),
              {
                title: t('Total images'),
                value: values.selectedObservations.length,
              },
            ]}
          />
          <br />
          <InfoList
            list={[
              {
                title: t('Number of patches'),
                value:
                  values.verticalPatches &&
                  values.horizontalPatches &&
                  values.verticalPatches * values.horizontalPatches,
              },
              {
                title: t('Classes priority'),
                value: values.patchesClassPriority?.join(', '),
              },
            ]}
          />

          <PatchStrategySelect fieldName="patchesStrategy" />

          <Row>
            <Col>
              <div id="divPatchesConfigurationId">
                <Button
                  buttonType="button"
                  label={t('Configure patches')}
                  type="default"
                  onClick={() => setPatchesConfigurationModal(true)}
                  icon={<FontAwesomeIcon icon={faTh} />}
                  disabled={
                    isSubmitting || values.selectedObservations.length === 0
                  }
                />
              </div>

              {patchesConfigurationModal && (
                <PatchesConfigurationModal
                  selectedObservations={values.selectedObservations}
                  setModal={setPatchesConfigurationModal}
                  setFieldValue={setFieldValue}
                  initialValues={{
                    horizontalSplit: values.horizontalPatches,
                    verticalSplit: values.verticalPatches,
                    overlap: values.patchesOverlap,
                  }}
                />
              )}
            </Col>
          </Row>

          <Row>
            <Col>
              <div id="divClassesPriorityId">
                <Button
                  buttonType="button"
                  label={t('Change classes priority')}
                  type="default"
                  onClick={() => setClassesPriorityModal(true)}
                  icon={<FontAwesomeIcon icon={faSortNumericUp} />}
                  disabled={isSubmitting}
                />
              </div>
              {classesPriorityModal && (
                <ClassesPriorityModal
                  setModal={setClassesPriorityModal}
                  setFieldValue={setFieldValue}
                  initialValues={values.patchesClassPriority || []}
                />
              )}
            </Col>
          </Row>

          {settings.preference__user_level === USER_LEVEL.expert && (
            <DatasetSelectField
              fieldName="dataset"
              productId={Number(productId)}
              onSelectedDatasets={setSelectedDatasets}
            />
          )}

          <BackboneSelect fieldName="backbone" productId={product.id} />

          <FP16Switch fieldName="fp16" />
        </>
      )}
      validate={(values) => {
        const errors: FormikErrors<{ [field: string]: string }> = {}

        if (!values.patchesStrategy) {
          errors.patchesStrategy = t('This field is required')
        }

        const len = countValidAnnotationObservations(
          values.selectedObservations
        )
        if (len < requiredImages) {
          errors.selectedObservations = t(
            'You need to use at least {{ count }} images with a valid annotation',
            { count: requiredImages }
          )
        } else if (len > MAX_TRAINING_IMAGES) {
          errors.selectedObservations = t(
            'To avoid long training times, you can select up to {{ count }} images',
            { count: MAX_TRAINING_IMAGES }
          )
        }

        const notAnnotatedObservations = countNotAnnotatedObservations(
          values.selectedObservations
        )

        if (notAnnotatedObservations > 0) {
          errors.selectedObservations = t(
            'You added {{ count }} image without annotation. Please remove or annotate it.',
            { count: notAnnotatedObservations }
          )
        }

        if (!values.backbone || values.backbone.length === 0) {
          errors.backbone = t('This field is required')
        }

        if (!values.name) {
          errors.name = t('This field is required')
        }

        if (
          values.startTest &&
          values.selectedObservations.length === product.observationsNumber
        ) {
          errors.startTest = t(
            'Deselect at least one image if you want to start a test with unselected images. Otherwise, deselect this option.'
          )
        }

        const trainingDurations = getEstimatedTrainingsDuration({
          productType: product.type,
          numOfImages: values.selectedObservations.length,
          backbones: values.backbone?.map((b) => ({
            code: b.code,
            label: b.label,
          })),
          precision: values.fp16 ? 'fp16' : 'fp32',
        })

        if (Object.keys(errors).length === 0) {
          setEstimatedTrainingsDuration(trainingDurations)
        }

        if (
          trainingDurations.find((d) => d.sec > 240 * 60) // 4 hours
        ) {
          errors.selectedObservations = t(
            'The estimated training time is too long. Please select fewer images or lower the resolution (if possible).'
          )
        }

        return errors
      }}
      estimatedTrainingsDuration={estimatedTrainingsDuration}
      hasNonAnnotatedImagesInDataset={hasNonAnnotatedImagesInDataset}
      onSubmit={(values, setSubmitting) => {
        const {
          selectedObservations,
          horizontalPatches,
          verticalPatches,
          patchesOverlap,
          patchesStrategy,
          patchesClassPriority,
          backbone,
        } = values
        if (
          horizontalPatches !== undefined &&
          verticalPatches !== undefined &&
          patchesOverlap !== undefined &&
          patchesStrategy !== undefined &&
          patchesClassPriority !== undefined &&
          backbone !== undefined
        ) {
          const backbones: BackboneSelectOption[] = (values.backbone as unknown) as BackboneSelectOption[]
          backbones.forEach((b: BackboneSelectOption) => {
            let metadata: Metadata = {
              horizontalPatches,
              verticalPatches,
              patchesOverlap,
              patchesStrategyId: Number(patchesStrategy.value),
              patchesClassPriority,
            }
            if (values.fp16) metadata = { ...metadata, precision: 'fp16' }
            createTraining(
              Number(projectId),
              Number(productId),
              Number(b.id),
              selectedObservations.map((o) => o.id),
              setSubmitting,
              history.push,
              metadata,
              values.name,
              values.notes,
              values.startTest,
              selectedDatasets.length > 0
                ? selectedDatasets.map((id) => Number(id))
                : undefined
            )
          })
        }
      }}
    >
      {/* overrides the original ObservationSelectField to handle dataset */}
      <ObservationsSelectField
        name="selectedObservations"
        productType={product.type}
        productId={Number(productId)}
        dataset={selectedDatasets.length > 0 ? selectedDatasets : undefined}
        inTraining
        inTest={false}
        onNonAnnotatedImagesInDataset={setHasNonAnnotatedImagesInDataset}
      />
    </TrainingNewForm>
  ) : null
}

export default NewPatchTraining
